import axios, { AxiosRequestConfig } from 'axios';
import { Serializable } from '@/models/core/serializable';
import { cookieService } from '@/service/cookie.service';
import { storageService } from '@/service/storage/storage.service';
import { PagingData } from '@/models/core/paging-data';
import { GEnum } from '@/constants/enum.defination';
import { Header } from '@/constants/constants';
import { DataList } from '@/models/core/data-list';
import { localStorageService } from '@/service/storage/local-storage.service';

type errorHandlerFunc = (error: any) => any;

const baseURL = process.env.VUE_APP_API_ENDPOINT || location.protocol + '//' + location.host;
//const baseURL = "https://dashboard-dev.aigrow.jp";
let apiErrorHandler: errorHandlerFunc;

export const API = axios.create({
  baseURL: baseURL + '/api/v3/academic',
  withCredentials: true,
  timeout: 600000,
});

export function setApiErrorHandler(handler: errorHandlerFunc) {
  apiErrorHandler = handler;
}

API.interceptors.request.use(
  config => {
    if (config.headers[Header.SchoolYear] === undefined) {
      // API個別で設定していなかった場合
      config.headers[Header.SchoolYear] = cookieService.schoolYear;
    }
    config.headers[Header.Token] = localStorageService.authToken || undefined;
    // config.headers.Lang = cookieService.language;
    if (config.params) {
      config.params['lang'] = cookieService.language;
    }

    //When login from igs dashboard,admin auth token should be added into cookie
    if (storageService.existsAdminAuthToken) {
      config.headers[storageService.ADMIN_AUTH_TOKEN] = storageService.adminAuthToken || undefined;
    }

    return config;
  },
  error => Promise.reject(error)
);

API.interceptors.response.use(undefined, err => {
  if (apiErrorHandler) {
    apiErrorHandler(err);
  }
  if (err.response && err.response.status && err.response.status === GEnum.HttpStatus.BadRequest) {
    // 処理継続のため
    return err.response;
  }
  if (err.response) {
    return Promise.reject(err.response.data);
  } else {
    return Promise.reject(err);
  }
});

export class ApiService {
  public get<T extends Serializable<T>>(type: { new (): T }, path: string, params: any, headers?: any): Promise<T> {
    const config: any = headers === undefined ? { params } : { params, headers };
    return API.get<T>(path, config).then(value => {
      return new type().deserialize(value.data);
    });
  }

  public getDynamic<D>(path: string, params: any): Promise<D> {
    return API.get<D>(path, { params }).then(value => {
      return value.data;
    });
  }

  public getList<T extends Serializable<T>>(type: { new (): T }, path: string, params: any, headers?: any): Promise<T[]> {
    const config: any = headers === undefined ? { params } : { params, headers };
    return API.get<DataList<T>>(path, config as AxiosRequestConfig).then(value => {
      return value.data.data.map(d => new type().deserialize(d));
    });
  }

  public getPagingData<T extends Serializable<T>>(type: { new (): T }, path: string, params: any): Promise<PagingData<T>> {
    return API.get<PagingData<T>>(path, { params }).then(value => {
      let result = new PagingData<T>();
      result.data = value.data.data.map((v: any) => new type().deserialize(v));
      result.totalRecords = value.data.totalRecords;
      return result;
    });
  }

  public getWithReject<T extends Serializable<T>>(type: { new (): T }, path: string, data?: any): Promise<T> {
    return API.get<T>(path, data).then(value => {
      if (value.status !== GEnum.HttpStatus.OK) {
        return Promise.reject(value);
      }
      return new type().deserialize(value.data);
    });
  }

  public delete(path: string, params: any): Promise<any> {
    return API.delete(path, { params }).then(value => value.data);
  }

  public head(path: string, params: any): Promise<any> {
    return API.head(path, { params }).then(value => value.data);
  }

  public post<T extends Serializable<T>>(type: { new (): T }, path: string, data?: any): Promise<T> {
    return API.post<T>(path, data).then(value => {
      if (value.status !== GEnum.HttpStatus.OK) {
        return Promise.reject(value);
      }
      return new type().deserialize(value.data);
    });
  }

  public put<T extends Serializable<T>>(type: { new (): T }, path: string, data?: any): Promise<T> {
    return API.put<T>(path, data).then(value => {
      if (value.status !== GEnum.HttpStatus.OK) {
        return Promise.reject(value);
      }
      return new type().deserialize(value.data);
    });
  }

  public putJSON<T extends Serializable<T>>(type: { new (): T }, path: string, data?: any): Promise<T> {
    return API.put<T>(path, data).then(value => {
      return new type().deserialize(value.data);
    });
  }

  public patch<T extends Serializable<T>>(type: { new (): T }, path: string, data?: any): Promise<T> {
    return API.patch<T>(path, data).then(value => {
      return new type().deserialize(value.data);
    });
  }

  public postJson<T extends Serializable<T>>(type: { new (): T }, path: string, data?: any): Promise<T> {
    return API.post<T>(path, data).then(value => {
      return new type().deserialize(value.data);
    });
  }

  public postFile<T extends Serializable<T>>(type: { new (): T }, path: string, formData: FormData): Promise<T> {
    return API.post<T>(path, formData).then((value: any) => {
      //暫定対応 APIのエラー制御見直し後に改善
      if (value.status === GEnum.HttpStatus.BadRequest) {
        // エラー時も処理を継続させるための処理
        return undefined;
      }
      if (value && value.status === GEnum.HttpStatus.OK) {
        return value;
      }
      return new type().deserialize(value.data);
    });
  }

  public loadExcelFile<T extends Serializable<T>>(type: { new (): T }, path: string, formData: FormData): Promise<T[]> {
    return API.post<T[]>(path, formData).then(value => {
      //暫定対応 APIのエラー制御見直し後に改善
      if (value.status === GEnum.HttpStatus.BadRequest) {
        // エラー時も処理を継続させるための処理
        return undefined;
      }
      // return new type().deserialize(value.data);
      return value.data.map((v: any) => new type().deserialize(v));
    });
  }

  public putFile<T extends Serializable<T>>(type: { new (): T }, path: string, formData: FormData): Promise<T> {
    return API.put<T>(path, formData).then(value => {
      return new type().deserialize(value.data);
    });
  }

  public downloadFile(fullPath: string, params: any): Promise<Blob> {
    return API.get(fullPath, {
      params: params,
      responseType: 'blob',
    } as AxiosRequestConfig).then(res => new Blob([res.data]));
  }
}
